import { apiSlice } from "./apiSlice";
import { DOCTORS_URL } from "../../constants/urls";
import { DOCTOR_STATUS_CHANGE_URL } from "../../constants/urls";
import { CKYS_URL } from "../../constants/urls";

export const doctorApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getDoctors: builder.query({
      query: () => ({
        url: `${DOCTORS_URL}`,
        method: "GET",
      }),
    }),
    /** Öne çıkan / önerilen doktorlar (Mobile API: sadece Approved, UpdatePending). Panel anasayfasında kullanılır. */
    getFeaturedDoctors: builder.query({
      query: (count = 6) => ({
        url: `${DOCTORS_URL}/featured?count=${count}`,
        method: "GET",
      }),
    }),
    getDoctorsAll: builder.query({
      query: (q) => ({
        url: `${DOCTORS_URL}/all?${q ? q : ""}`,
        method: "GET"
      }),
      providesTags: ['Doctor'],
    }),
    getDoctorsByStatus: builder.query({
      query: (status) => ({
        url: `${DOCTORS_URL}/byStatus/${status}`,
        method: "GET",
      }),
    }),
    getDoctorById: builder.query({
      query: (id) => ({
        url: `${DOCTORS_URL}/${id}`,
        method: "GET",
      }),
    }),
    addDoctor: builder.mutation({
      query: (data) => ({
        url: `${DOCTORS_URL}`,
        method: "POST",
        body: data,
      }),
    }),
    updateDoctorContactProfile: builder.mutation({
      query: (data) => ({
        url: `${DOCTORS_URL}/profile/contact`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["Doctor"],
    }),
    updateDoctorIdentityProfile: builder.mutation({
      query: (data) => ({
        url: `${DOCTORS_URL}/profile/identity`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["Doctor"],
    }),
    updateDoctorLocalizationProfile: builder.mutation({
      query: (data) => ({
        url: `${DOCTORS_URL}/profile/localization`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["Doctor"],
    }),
    updateDoctorCvProfile: builder.mutation({
      query: (data) => ({
        url: `${DOCTORS_URL}/profile/cv`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["Doctor"],
    }),
    updateDoctorSpecialtyProfile: builder.mutation({
      query: (data) => ({
        url: `${DOCTORS_URL}/profile/specialty`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["Doctor"],
    }),
    updateBillingInfo: builder.mutation({
      query: (data) => ({
        url: `${DOCTORS_URL}/profile/billing`,
        method: 'PUT',
        body: data,
      }),
    }),
    updateDoctor: builder.mutation({
      query: (data) => (
        {
          url: `${DOCTORS_URL}`,
          method: "PUT",
          body: data,
        }),
    }),
    getDoctorProfile: builder.query({
      query: () => ({
        url: `${DOCTORS_URL}/profile`,
        method: "GET",
      }),
      // transformResponse: (response) => ({ res: response.data, error: response.error, isSuccess: response.isSuccess })
    }),
    getAvailableLanguages: builder.query({
      query: () => ({
        url: `${DOCTORS_URL}/languages`,
        method: "GET",
      }),
    }),
    removeDoctor: builder.mutation({
      query: (id) => ({
        url: `${DOCTORS_URL}/${id}`,
        method: "DELETE",
      }),
    }),
    setDoctorActiveStatus: builder.mutation({
      query: ({ id, isActive }) => ({
        url: `${DOCTORS_URL}/${id}/active-status`,
        method: "PUT",
        body: { isActive },
      }),
      invalidatesTags: ['Doctor'],
    }),
    changePassword: builder.mutation({
      query: (data) => ({
        url: `${DOCTORS_URL}/changePassword`,
        method: "POST",
        body: data,
      }),
    }),
    uploadPhoto: builder.mutation({
      query: (data) => ({
        url: `${DOCTORS_URL}/profile/photo`,
        method: "POST",
        body: data,
      }),
    }),
    checkEligibility: builder.mutation({
      query: (data) => ({
        url: `${DOCTORS_URL}/iseligible`,
        method: "POST",
        body: data
      })
    }),
    addUserStatusChangeInfo: builder.mutation({
      query: (data) => ({ url: DOCTOR_STATUS_CHANGE_URL, method: "POST", body: data }),
      invalidatesTags: ['Doctor'],
    }),
    getCalismaBilgisi: builder.mutation({
      query: (data) => ({
        url: `${CKYS_URL}/CalismaBilgisi`,
        method: "POST",
        body: data,
      }),
    }),
    getTahsilBilgisi: builder.mutation({
      query: (data) => ({
        url: `${CKYS_URL}/TahsilBilgisi`,
        method: "POST",
        body: data,
      }),
    }),
    approveDoctorUpdateRequest: builder.mutation({
      query: (data) => ({
        url: `${DOCTORS_URL}/ApproveRegistrationUpdate`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ['Doctor'],
    }),
    getRegistrationUpdateRequests: builder.query({
      query: (status) => ({
        url: status
          ? `${DOCTORS_URL}/RegistrationUpdateRequests?status=${encodeURIComponent(status)}`
          : `${DOCTORS_URL}/RegistrationUpdateRequests`,
        method: "GET",
      }),
      providesTags: ['Doctor'],
    }),
    addDoctorEducation: builder.mutation({
      query: (data) => ({
        url: `${DOCTORS_URL}/educations`,
        method: "POST",
        body: data,
      }),
    }),
    updateDoctorEducation: builder.mutation({
      query: ({ index, data }) => ({
        url: `${DOCTORS_URL}/educations/${index}`,
        method: "PUT",
        body: data,
      }),
    }),
    deleteDoctorEducation: builder.mutation({
      query: (index) => ({
        url: `${DOCTORS_URL}/educations/${index}`,
        method: "DELETE",
      }),
    }),
    getDoctorEducations: builder.query({
      query: ({ status } = {}) => {
        const queryString = status ? `?status=${encodeURIComponent(status)}` : "";
        return {
          url: `${DOCTORS_URL}/educations${queryString}`,
          method: "GET",
        };
      },
    }),
    setDoctorEducationStatus: builder.mutation({
      query: ({ doctorId, index, data }) => ({
        url: `${DOCTORS_URL}/educations/${doctorId}/${index}/status`,
        method: "PUT",
        body: data,
      }),
    }),
    addDoctorExperience: builder.mutation({
      query: (data) => ({
        url: `${DOCTORS_URL}/experiences`,
        method: "POST",
        body: data,
      }),
    }),
    updateDoctorExperience: builder.mutation({
      query: ({ index, data }) => ({
        url: `${DOCTORS_URL}/experiences/${index}`,
        method: "PUT",
        body: data,
      }),
    }),
    deleteDoctorExperience: builder.mutation({
      query: (index) => ({
        url: `${DOCTORS_URL}/experiences/${index}`,
        method: "DELETE",
      }),
    }),
    addDoctorPublication: builder.mutation({
      query: (data) => ({
        url: `${DOCTORS_URL}/publications`,
        method: "POST",
        body: data,
      }),
    }),
    updateDoctorPublication: builder.mutation({
      query: ({ index, data }) => ({
        url: `${DOCTORS_URL}/publications/${index}`,
        method: "PUT",
        body: data,
      }),
    }),
    deleteDoctorPublication: builder.mutation({
      query: (index) => ({
        url: `${DOCTORS_URL}/publications/${index}`,
        method: "DELETE",
      }),
    }),
    addDoctorLanguage: builder.mutation({
      query: (data) => ({
        url: `${DOCTORS_URL}/languages`,
        method: "POST",
        body: data,
      }),
    }),
    updateDoctorLanguage: builder.mutation({
      query: ({ index, data }) => ({
        url: `${DOCTORS_URL}/languages/${index}`,
        method: "PUT",
        body: data,
      }),
    }),
    deleteDoctorLanguage: builder.mutation({
      query: (index) => ({
        url: `${DOCTORS_URL}/languages/${index}`,
        method: "DELETE",
      }),
    }),
    updateAppointmentScheduleRange: builder.mutation({
      query: (data) => ({
        url: `${DOCTORS_URL}/profile/schedule`,
        method: "PUT",
        body: data,
      }),
    }),
  }),
});

export const {
  useUploadPhotoMutation,
  useChangePasswordMutation,
  useUpdateDoctorMutation,
  useGetDoctorsByStatusQuery,
  useLazyGetDoctorsByStatusQuery,
  useGetFeaturedDoctorsQuery,
  useRemoveDoctorMutation,
  useSetDoctorActiveStatusMutation,
  useAddDoctorMutation,
  useGetDoctorByIdQuery,
  useGetDoctorProfileQuery,
  useLazyGetDoctorProfileQuery,
  useGetAvailableLanguagesQuery,
  useGetDoctorsQuery,
  useLazyGetDoctorsQuery,
  useLazyGetDoctorsAllQuery,
  useLazyGetDoctorByIdQuery,
  useUpdateDoctorContactProfileMutation,
  useUpdateDoctorIdentityProfileMutation,
  useUpdateDoctorLocalizationProfileMutation,
  useUpdateDoctorCvProfileMutation,
  useUpdateDoctorSpecialtyProfileMutation,
  useUpdateBillingInfoMutation,
  useCheckEligibilityMutation,
  useAddUserStatusChangeInfoMutation,
  useGetCalismaBilgisiMutation,
  useGetTahsilBilgisiMutation,
  useApproveDoctorUpdateRequestMutation,
  useGetRegistrationUpdateRequestsQuery,
  useLazyGetRegistrationUpdateRequestsQuery,
  useAddDoctorEducationMutation,
  useUpdateDoctorEducationMutation,
  useDeleteDoctorEducationMutation,
  useGetDoctorEducationsQuery,
  useSetDoctorEducationStatusMutation,
  useAddDoctorExperienceMutation,
  useUpdateDoctorExperienceMutation,
  useDeleteDoctorExperienceMutation,
  useAddDoctorPublicationMutation,
  useUpdateDoctorPublicationMutation,
  useDeleteDoctorPublicationMutation,
  useAddDoctorLanguageMutation,
  useUpdateDoctorLanguageMutation,
  useDeleteDoctorLanguageMutation,
  useUpdateAppointmentScheduleRangeMutation,
} = doctorApiSlice;
